import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { NSLabel } from 'bricks';
import { Row, Col } from 'reactstrap';
import NSInputNumberStepper from 'bricks/NSInputNumberStepper/NSInputNumberStepper';
import './ProFormaAssumptions.scss';
import { IAssumptions } from 'views/ProFormaTable/types';
import { ProFormaQuickStartValues } from 'views/public/ProFormaQuickStart/types';
import { useProFormaSocketContext } from '../../socketContext/ProFormaSocketProvider';
import MetricOutputCard from '../../../MetricOutputCard/MetricOutputCard';

export interface IProFormaAssumptionsProps {
    proFormaSignUpData?: ProFormaQuickStartValues;
}

const ProFormaAssumptions = ({ proFormaSignUpData } : IProFormaAssumptionsProps) => {
    const { handleUpdateProFormaAssumptions } = useProFormaSocketContext();
    const [marketCapRate, setMarketCapRate] = useState<number>(0);
    const [projectedCapRate, setProjectedCapRate] = useState<number>(0);
    const [sellingCosts, setSellingCosts] = useState<number>(0);

    const { disposition } = useProFormaSocketContext();

    useEffect(() => {
        if (disposition) {
            setMarketCapRate(disposition.marketCapitalizationRate?.current);
            setProjectedCapRate(disposition.marketCapitalizationRate?.onSale);
            setSellingCosts(disposition.sellingCostsPercent);
        } else if (proFormaSignUpData?.basicInformation) {
            setMarketCapRate(proFormaSignUpData?.basicInformation?.currentMarketCapRate);
            setProjectedCapRate(proFormaSignUpData?.basicInformation?.projectedCapRateAtSale);
            setSellingCosts(proFormaSignUpData?.basicInformation?.sellingCosts);
        }
    }, [disposition, proFormaSignUpData]);

    const labelClassNameNoWrap = 'pl-0 col-4 my-auto font-weight-normal text-dark text-nowrap';

    const updateAssumptions = (value: number, field: keyof IAssumptions) => {
        handleUpdateProFormaAssumptions({
            [field]: value,
        });
    };

    return (
        <Row className="mx-n1 px-3">
            <Col className="px-1 my-3" sm={12} md={6}>
                <div>
                    <div className="d-flex align-items-center">
                        <NSLabel className={labelClassNameNoWrap}>Current market cap rate</NSLabel>
                        <NSInputNumberStepper
                            max={100}
                            percentage
                            name="marketCapRate"
                            value={marketCapRate || 0}
                            onChange={(value: number) => {
                                setMarketCapRate(value);
                                updateAssumptions(value, 'currentMarketCapRate');
                            }}
                            min={0}
                            step={0.1}
                            inputGroupClassName="ProFormaAssumptions__input-width"
                        />
                    </div>

                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={labelClassNameNoWrap}>Projected cap rate at sale</NSLabel>
                        <NSInputNumberStepper
                            max={100}
                            percentage
                            name="projectedCapRate"
                            value={projectedCapRate || 0}
                            onChange={(value: number) => {
                                setProjectedCapRate(value);
                                updateAssumptions(value, 'projectedCapRateAtSale');
                            }}
                            min={0}
                            step={0.1}
                            inputGroupClassName="ProFormaAssumptions__input-width"
                        />
                    </div>

                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={labelClassNameNoWrap}>Selling costs</NSLabel>
                        <NSInputNumberStepper
                            max={100}
                            percentage
                            name="sellingCosts"
                            value={sellingCosts || 0}
                            onChange={(value: number) => {
                                setSellingCosts(value);
                                updateAssumptions(value, 'sellingCosts');
                            }}
                            min={0}
                            step={0.1}
                            inputGroupClassName="ProFormaAssumptions__input-width"
                        />
                    </div>
                </div>
            </Col>
            <Col className="px-1 mt-3 pb-2" sm={12} md={6}>
                <div>
                    <MetricOutputCard
                        label="Sale month"
                        value={disposition?.proFormaSaleDate && format(disposition?.proFormaSaleDate, 'MMMM yyyy')}
                        showInProgress={!disposition?.proFormaSaleDate}
                    />
                </div>
            </Col>
        </Row>
    );
};

export default ProFormaAssumptions;
